import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../manage.css";
import axios from "axios";
import { setPostOnEdit, updatePost } from "../../Redux/Actions/postsActions";

const EditPost = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const postOnEdit = useSelector((state) => state.posts.postOnEdit);

  const loggedUser = useSelector((state) => state.users.loggedUser);

  const isLoggedIn = loggedUser && Object.keys(loggedUser).length > 0;

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [imagesNr, setImagesNr] = useState(postOnEdit?.nrImages || 0);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    createdOn: "",
    slug: "",
    images: [],
    coverImage: "",
  });

  const textareaRef = useRef(null);

  // Separate refs
  const galleryInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  // Load post data
  useEffect(() => {
    if (!postOnEdit || !postOnEdit._id) return;

    setFormData({
      title: postOnEdit.title || "",
      description: postOnEdit.description || "",
      createdOn: postOnEdit.createdOn || "",
      slug: postOnEdit.slug || "",
      images: [],
      coverImage: postOnEdit.coverImage || "",
    });

    setImagesNr(postOnEdit.nrImages || 0);
  }, [postOnEdit]);

  // Fetch images
  useEffect(() => {
    const fetchImages = async () => {
      if (!postOnEdit || !postOnEdit._id) return;

      try {
        setLoading(true);

        const token = localStorage.getItem("token");

        const response = await axios.get(
          `https://api.arkiva.gov.al/api/posts/images/${postOnEdit.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.images) {
          const coverImagePath = response.data.images.find((img) =>
            img.includes(postOnEdit.coverImage),
          );

          const galleryImages = response.data.images.filter(
            (img) => img !== coverImagePath,
          );

          setFormData((prevData) => ({
            ...prevData,
            coverImage: coverImagePath || prevData.coverImage,
            images: galleryImages,
          }));
        }
      } catch (error) {
        console.error("Error fetching images:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [postOnEdit]);

  // Generate slug
  useEffect(() => {
    const generateSlug = (input) => {
      return input
        .toLowerCase()
        .replace(/ë/g, "e")
        .replace(/ç/g, "c")
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
    };

    setFormData((prevData) => ({
      ...prevData,
      slug: generateSlug(prevData.title),
    }));
  }, [formData.title]);

  const resizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";

      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    resizeTextarea();
  }, [formData.description]);

  const validateForm = () => {
    const newErrors = {};

    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

    const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    const isLeapYear = (year) => {
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    };

    const isValidDate = (day, month, year) => {
      if (month < 1 || month > 12) {
        return false;
      }

      const maxDays =
        month === 2 && isLeapYear(year) ? 29 : monthDays[month - 1];

      return day >= 1 && day <= maxDays;
    };

    if (!formData.title.trim()) {
      newErrors.title = "Titulli nuk mund të jetë bosh!";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Përshkrimi nuk mund të jetë bosh!";
    }

    if (!formData.createdOn.trim()) {
      newErrors.createdOn = "Data e krijimit nuk mund të jetë bosh!";
    } else {
      const match = formData.createdOn.match(dateRegex);

      if (!match) {
        newErrors.createdOn =
          "Data e krijimit duhet të jetë në formatin 'dd/mm/yyyy' ose 'd/m/yyyy'!";
      } else {
        const [, day, month, year] = match.map(Number);

        const enteredDate = new Date(year, month - 1, day);

        const currentDate = new Date();

        if (!isValidDate(day, month, year)) {
          newErrors.createdOn = "Data e krijimit nuk është e saktë!";
        } else if (enteredDate > currentDate) {
          newErrors.createdOn =
            "Data e krijimit nuk mund të jetë në të ardhmen!";
        }
      }
    }

    if (!formData.coverImage) {
      newErrors.coverImage = "Kopertina nuk mund të jetë bosh!";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // DELETE GALLERY IMAGE
  const handleDeleteImage = async (index) => {
    const imagePath = formData.images[index];

    const token = localStorage.getItem("token");

    try {
      setLoading(true);

      const response = await fetch(
        "https://api.arkiva.gov.al/api/posts/delete-image",
        {
          method: "DELETE",

          headers: {
            "Content-Type": "application/json",

            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify({
            imagePath: `/Assets/Images/Posts/${postOnEdit.id}/${imagePath}`,
            postId: postOnEdit._id,
            folderId: postOnEdit.id,
          }),
        },
      );

      if (!response.ok) {
        const errorResponse = await response.json();

        console.error("Failed to delete image:", errorResponse);

        return;
      }

      const updatedImages = formData.images.filter((_, i) => i !== index);

      setImagesNr((prev) => Math.max(prev - 1, 0));

      setFormData((prevData) => ({
        ...prevData,
        images: updatedImages,
      }));

      const refreshedData = await axios.get(
        `https://api.arkiva.gov.al/api/posts/${postOnEdit._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (refreshedData.data) {
        dispatch(setPostOnEdit(refreshedData.data));

        dispatch(updatePost(refreshedData.data));
      }
    } catch (error) {
      console.error("Error deleting image:", error);
    } finally {
      setLoading(false);
    }
  };

  // UPDATE POST
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!postOnEdit?._id) {
      console.error("Error: _id is undefined.");
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const response = await axios.put(
        `https://api.arkiva.gov.al/api/posts/update/${postOnEdit._id}`,
        {
          title: formData.title,
          description: formData.description,
          createdOn: formData.createdOn,
          slug: formData.slug,
          nrImages: imagesNr,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data) {
        dispatch(updatePost(response.data));

        dispatch(setPostOnEdit({}));

        navigate("/manage/aktivitete");
      }
    } catch (error) {
      console.error("Error updating post:", error);

      if (error.response?.status === 401) {
        navigate("/login");
      } else if (error.response?.status === 403) {
        navigate("/unauthorized");
      }
    } finally {
      setLoading(false);
    }
  };

  // ADD GALLERY IMAGE
  const onFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const uploadFormData = new FormData();

      uploadFormData.append("file", selectedFile);

      uploadFormData.append("folderId", postOnEdit.id);

      const response = await axios.post(
        "https://api.arkiva.gov.al/api/posts/upload",
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { fileName, filePath } = response.data;

      setFormData((prevData) => ({
        ...prevData,
        images: [...prevData.images, filePath],
      }));

      setImagesNr((prev) => prev + 1);
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setLoading(false);

      if (galleryInputRef.current) {
        galleryInputRef.current.value = "";
      }
    }
  };

  // CLICK REPLACE COVER
  const handleReplaceCoverImage = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");

    try {
      setLoading(true);

      if (formData.coverImage) {
        const deleteResponse = await fetch(
          "https://api.arkiva.gov.al/api/posts/delete-image",
          {
            method: "DELETE",

            headers: {
              "Content-Type": "application/json",

              Authorization: `Bearer ${token}`,
            },

            body: JSON.stringify({
              imagePath: `/Assets/Images/Posts/${postOnEdit.id}/${postOnEdit.coverImage}`,
              postId: postOnEdit._id,
              folderId: postOnEdit.id,
            }),
          },
        );

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json();

          console.warn("Cover deletion failed:", errorData);
        }
      }

      coverInputRef.current?.click();
    } catch (error) {
      console.error("Error replacing cover image:", error);

      coverInputRef.current?.click();
    } finally {
      setLoading(false);
    }
  };

  // UPLOAD NEW COVER
  const handleCoverImageUpload = async (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    const token = localStorage.getItem("token");

    try {
      setLoading(true);

      const uploadFormData = new FormData();

      uploadFormData.append("file", selectedFile);

      uploadFormData.append("folderId", postOnEdit.id);

      const uploadResponse = await axios.post(
        "https://api.arkiva.gov.al/api/posts/upload",
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { fileName } = uploadResponse.data;

      const updateResponse = await axios.put(
        `https://api.arkiva.gov.al/api/posts/update/${postOnEdit._id}`,
        {
          coverImage: fileName,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (updateResponse.data) {
        const newCoverPath = `/Assets/Images/Posts/${postOnEdit.id}/${fileName}`;

        setFormData((prevData) => ({
          ...prevData,
          coverImage: newCoverPath,
        }));

        dispatch(
          setPostOnEdit({
            ...postOnEdit,
            coverImage: fileName,
          }),
        );
      }
    } catch (error) {
      console.error("Error uploading cover image:", error);
    } finally {
      setLoading(false);

      if (coverInputRef.current) {
        coverInputRef.current.value = "";
      }
    }
  };

  const handleCancel = () => {
    navigate("/manage/aktivitete");
  };

  const returnHome = () => {
    dispatch(setPostOnEdit({}));

    navigate("/manage/aktivitete");
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="edit-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="return">
        <button type="button" onClick={returnHome}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
      </div>

      <p>Edito Aktivitet</p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Titulli</label>

          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
          />

          {errors.title && <small className="error">{errors.title}</small>}
        </div>

        <div className="form-group">
          <label htmlFor="description">Përshkrimi</label>

          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            ref={textareaRef}
            onInput={resizeTextarea}
            style={{
              width: "100%",
              overflow: "hidden",
            }}
          />

          {errors.description && (
            <small className="error">{errors.description}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="createdOn">Data e krijimit</label>

          <input
            type="text"
            id="createdOn"
            name="createdOn"
            value={formData.createdOn}
            onChange={handleChange}
          />

          {errors.createdOn && (
            <small className="error">{errors.createdOn}</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="slug">Slug (Path)</label>

          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            disabled
          />
        </div>

        <div className="form-group gallery">
          <label>Imazhet</label>

          <div className="images-container">
            {formData.images.map((image, index) => (
              <div className="image-wrapper" key={image || index}>
                <img
                  src={image}
                  alt={`Imazhi ${index + 1}`}
                  className="preview"
                  style={{
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                />

                <button
                  type="button"
                  className="delete-button"
                  onClick={() => handleDeleteImage(index)}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            ))}
          </div>

          <div className="image-upload">
            <label htmlFor="galleryUpload">Shto imazhe</label>

            <input
              type="file"
              id="galleryUpload"
              ref={galleryInputRef}
              accept="image/*"
              onChange={onFileChange}
            />
          </div>
        </div>

        <div className="form-group cover">
          <label>Kopertina</label>

          {formData.coverImage && (
            <div className="image-wrapper">
              <img
                src={formData.coverImage}
                alt="Kopertina"
                className="preview"
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "cover",
                }}
              />
            </div>
          )}

          <input
            type="file"
            id="coverUpload"
            ref={coverInputRef}
            accept="image/*"
            onChange={handleCoverImageUpload}
            style={{
              display: "none",
            }}
          />

          {errors.coverImage && (
            <small className="error">{errors.coverImage}</small>
          )}

          <button
            type="button"
            className="replace"
            onClick={handleReplaceCoverImage}
          >
            Zëvendëso kopertinën
          </button>
        </div>

        <div className="edit-actions">
          <button type="submit" className="save">
            Ruaj ndryshimet
          </button>

          <button type="button" className="cancel" onClick={handleCancel}>
            Anullo
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPost;
